import { useMemo } from "react"
import { useAppSelector } from "../store/storeHooks"
import { selectSelectedLocale } from "./localizationSelectors"
import { ScaledAmount } from "../amounts/amountTypes"
import { movePointLeft, toDecimal } from "../amounts/scaledAmountLib"

type NumberFactoryOptions = Readonly<{
    locale: string
}>

export class NumberFactory {
    public readonly locale: string
    public readonly decimalSeparator: string
    public readonly groupSeparator: string
    private formatter: Intl.NumberFormat
    private percentFormatter: Intl.NumberFormat

    constructor({ locale }: NumberFactoryOptions) {
        this.locale = locale
        this.formatter = Intl.NumberFormat(locale)
        this.percentFormatter = Intl.NumberFormat(locale, {
            style: "percent",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })

        this.decimalSeparator = this.formatter.formatToParts(1.1)
            .find(part => part.type === "decimal")
            ?.value ?? "."

        this.groupSeparator = this.formatter.formatToParts(1000000)
            .find(part => part.type === "group")
            ?.value ?? ","
    }

    format(value: number): string {
        return this.formatter.format(value)
    }

    formatToDecimal(value: number, fractionDigits: number): string {
        const formatter = Intl.NumberFormat(this.locale, {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
        })

        return formatter.format(value)
    }

    formatPercentage(value: ScaledAmount, isFraction: boolean = false): string {
        // Percent formatter expects a fraction, so 25% would be .25 but we
        // might use 25; in such cases we need to rescale
        const fraction = isFraction ? value : movePointLeft(value, 2)
        return toDecimal(fraction, this.percentFormatter.format)
    }
}

type UseNumberOptions = Readonly<{
    locale?: string
}>

export function useNumber({ locale }: UseNumberOptions) {
    const selectedLocale = useAppSelector(selectSelectedLocale)
    const localeToUse = locale ?? selectedLocale
    const factory = useMemo(() => new NumberFactory({ locale: localeToUse }), [localeToUse])

    return factory
}
